"use client";

import { useState, useCallback, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  ProductFilters,
  FilterSection,
  ProductFiltersProps,
} from "@/lib/types/shop";

interface ExtendedProductFiltersProps extends ProductFiltersProps {
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  showSortInFilters?: boolean;
}

export function ProductFiltersComponent({
  filters,
  onFilterChange,
  onClearFilters,
  filterOptions,
  activeFiltersCount,
  isLoading = false,
  className,
  onSizeSystemChange,
  sortBy = "newest",
  onSortChange,
  showSortInFilters = false,
}: ExtendedProductFiltersProps) {
  // Use actual price range from filterOptions
  const actualMinPrice = filterOptions.priceRange?.min || 0;
  const actualMaxPrice = filterOptions.priceRange?.max || 1000000;
  
  const [priceValues, setPriceValues] = useState([
    filters.minPrice || actualMinPrice,
    filters.maxPrice || actualMaxPrice,
  ]);
  const [priceRange] = useState([actualMinPrice, actualMaxPrice]);

  const [openSections, setOpenSections] = useState<FilterSection>({
    category: true,
    subcategory: true,
    productType: true,
    brand: true,
    price: false,
    size: true,
    style: false,
    color: true,
    occasion: false,
    material: false,
    fitting: false,
    condition: true,
  });

  const toggleSection = useCallback((section: keyof FilterSection) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Reset price values when filters are cleared
  useEffect(() => {
    if (filters.minPrice === undefined && filters.maxPrice === undefined) {
      setPriceValues([actualMinPrice, actualMaxPrice]);
    }
  }, [filters.minPrice, filters.maxPrice, actualMinPrice, actualMaxPrice]);

  const handlePriceChange = useCallback(
    (values: number[]) => {
      setPriceValues(values);
      // Bug 2 fix: batch BOTH price changes via a single call to avoid race condition.
      // We call minPrice and maxPrice sequentially within the same event — but since
      // both are debounced in the parent, the last call (maxPrice) would overwrite
      // the first. Instead, we call them individually and let the parent's special
      // price-preservation logic in handleFilterChange handle it correctly.
      const minValue = values[0] === actualMinPrice ? undefined : values[0];
      const maxValue = values[1] === actualMaxPrice ? undefined : values[1];
      onFilterChange("minPrice", minValue);
      onFilterChange("maxPrice", maxValue);
    },
    [onFilterChange, actualMinPrice, actualMaxPrice]
  );

  // Bug 5 fix: toggle a size in/out of the sizes array (multi-select)
  const handleSizeToggle = useCallback(
    (size: string) => {
      if (size === "All") {
        onFilterChange("sizes", []);
        return;
      }
      const currentSizes: string[] = (filters.sizes as string[]) || [];
      const isSelected = currentSizes.includes(size);
      const newSizes = isSelected
        ? currentSizes.filter((s) => s !== size)
        : [...currentSizes, size];
      onFilterChange("sizes", newSizes);
    },
    [filters.sizes, onFilterChange]
  );

  const handleRadioChange = useCallback(
    (filterKey: keyof ProductFilters, value: string) => {
      const newValue = value === "All" ? undefined : value;
      onFilterChange(
        filterKey,
        filters[filterKey] === newValue ? undefined : newValue
      );
    },
    [filters, onFilterChange]
  );

  const handleCheckboxChange = useCallback(
    (filterKey: keyof ProductFilters, value: string) => {
      const currentValue = filters[filterKey] as string;
      const newValue = currentValue === value ? undefined : value;
      onFilterChange(filterKey, newValue);
    },
    [filters, onFilterChange]
  );

  const FilterRadioGroup = ({
    title,
    options,
    filterKey,
    sectionKey,
  }: {
    title: string;
    options: string[];
    filterKey: keyof ProductFilters;
    sectionKey: keyof FilterSection;
  }) => (
    <div className="mb-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between border-b border-gray-200"
        >
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          <ChevronDown
            className={cn(
              "h-3 w-3 text-gray-600 transition-transform duration-300",
              openSections[sectionKey] ? "rotate-180" : "-rotate-90"
            )}
          />
        </button>
        <div
          className={cn(
            "transition-all duration-300 ease-in-out overflow-hidden bg-white",
            openSections[sectionKey]
              ? "max-h-96 py-4 px-5"
              : "max-h-0 py-0 px-5"
          )}
        >
          <div
            className={cn(
              "space-y-0",
              options.length > 6
                ? "max-h-48 overflow-y-auto pr-2 scrollbar-light"
                : ""
            )}
            style={options.length > 6 ? {
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent'
            } : undefined}
          >
            {options.map((option) => (
              <label
                key={option}
                className="flex items-center cursor-pointer py-2 text-sm group"
              >
                <input
                  type="checkbox"
                  checked={
                    filters[filterKey] === option ||
                    (!filters[filterKey] && option === "All")
                  }
                  onChange={() => handleRadioChange(filterKey, option)}
                  className="mr-2.5 accent-black"
                />
                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                  {option}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={cn("w-full", className)}>
      <div className="bg-white p-0 rounded-none h-full flex flex-col">
        {/* Sort By Section - Only shown when showSortInFilters is true */}
        {showSortInFilters && onSortChange && (
          <div className="mb-4">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-900">Sort By</span>
              </div>
              <div className="p-4">
                <Select value={sortBy} onValueChange={onSortChange} disabled={isLoading}>
                  <SelectTrigger className="w-full h-10 bg-white border-gray-200 text-gray-900 focus:ring-gray-400">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="newest" className="text-gray-900 focus:bg-gray-100">Newest</SelectItem>
                    <SelectItem value="price_asc" className="text-gray-900 focus:bg-gray-100">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc" className="text-gray-900 focus:bg-gray-100">Price: High to Low</SelectItem>
                    <SelectItem value="rating" className="text-gray-900 focus:bg-gray-100">Highest Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Filter Content */}
        <div 
          className="flex-1 overflow-y-auto scrollbar-light"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db transparent'
          }}
        >
          {/* Category Filter — one group per industry when the shop spans
              more than one (Menu / Rooms / Shop…), the flat list otherwise. */}
          {filterOptions.categoryGroups && filterOptions.categoryGroups.length > 1 ? (
            filterOptions.categoryGroups.map((g, i) => (
              <FilterRadioGroup
                key={g.label}
                title={g.label}
                options={i === 0 ? ["All", ...g.categories] : g.categories}
                filterKey="category"
                sectionKey="category"
              />
            ))
          ) : (
            <FilterRadioGroup
              title="Category"
              options={filterOptions.categories}
              filterKey="category"
              sectionKey="category"
            />
          )}

          {/* Subcategory Filter */}
          {filters.category && filters.category !== "All" && filterOptions.subcategories.length > 1 && (
            <FilterRadioGroup
              title="Subcategory"
              options={filterOptions.subcategories}
              filterKey="subcategory"
              sectionKey="subcategory"
            />
          )}

          {/* Product Type Filter - Only show when subcategory is selected */}
          {filters.subcategory && filters.subcategory !== "All" && filterOptions.productTypes && filterOptions.productTypes.length > 1 && (
            <FilterRadioGroup
              title="Product Type"
              options={filterOptions.productTypes}
              filterKey="productType"
              sectionKey="productType"
            />
          )}

          {/* Condition — new or used, in the shop's own words (Bagz Phones,
              Oct 2026). Only when the shop sells two or more. */}
          {(filterOptions.conditions?.length ?? 0) > 1 && (
            <FilterRadioGroup
              title="Condition"
              options={["All", ...(filterOptions.conditions || [])]}
              filterKey="condition"
              sectionKey="condition"
            />
          )}

          {/* Size Filter */}
          <div className="mb-4">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection("size")}
                className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between border-b border-gray-200"
              >
                <span className="text-sm font-semibold text-gray-900">
                  Size
                  {/* Show count of selected sizes */}
                  {filters.sizes && filters.sizes.length > 0 && (
                    <span className="ml-2 text-xs bg-black text-white rounded-full px-1.5 py-0.5">
                      {filters.sizes.length}
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    "h-3 w-3 text-gray-600 transition-transform duration-300",
                    openSections.size ? "rotate-180" : "-rotate-90"
                  )}
                />
              </button>
              <div
                className={cn(
                  "transition-all duration-300 ease-in-out overflow-hidden bg-white",
                  openSections.size ? "max-h-96 py-4 px-5" : "max-h-0 py-0 px-5"
                )}
              >
                <div className="space-y-4">
                  {filterOptions.availableSizeSystems && onSizeSystemChange && (
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-600 font-medium">
                        Size System
                      </Label>
                      <Select
                        value={filterOptions.sizeSystem}
                        onValueChange={onSizeSystemChange}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="w-full h-8 text-xs bg-white border-gray-200 text-gray-900">
                          <SelectValue placeholder="Select sizing system" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {filterOptions.availableSizeSystems.map((system) => (
                            <SelectItem key={system} value={system} className="text-xs text-gray-900">
                              {system}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-600 font-medium">
                      Available Sizes
                      {/* Multi-select hint */}
                      <span className="ml-1 font-normal text-gray-400">(select multiple)</span>
                    </Label>
                    <div
                      className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1"
                      style={{ scrollbarWidth: "thin", scrollbarColor: "#d1d5db transparent" }}
                    >
                      {/* Bug 5 fix: multi-select size buttons */}
                      {filterOptions.sizes.map((size) => {
                        const selectedSizes: string[] = (filters.sizes as string[]) || [];
                        const isSelected = size === "All"
                          ? selectedSizes.length === 0
                          : selectedSizes.includes(size);
                        return (
                          <button
                            key={size}
                            onClick={() => handleSizeToggle(size)}
                            className={cn(
                              "px-3 py-2 border rounded text-xs cursor-pointer transition-all duration-200",
                              isSelected
                                ? "bg-black text-white border-black shadow-sm"
                                : "bg-white text-gray-700 border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                            )}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Color Filter */}
          <div className="mb-4">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection("color")}
                className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between border-b border-gray-200"
              >
                <span className="text-sm font-semibold text-gray-900">
                  Color
                </span>
                <ChevronDown
                  className={cn(
                    "h-3 w-3 text-gray-600 transition-transform duration-300",
                    openSections.color ? "rotate-180" : "-rotate-90"
                  )}
                />
              </button>
              <div
                className={cn(
                  "transition-all duration-300 ease-in-out overflow-hidden bg-white",
                  openSections.color
                    ? "max-h-96 py-4 px-5"
                    : "max-h-0 py-0 px-5"
                )}
              >
                <div
                  className="flex gap-2.5 flex-wrap max-h-44 overflow-y-auto pr-1"
                  style={{ scrollbarWidth: "thin", scrollbarColor: "#d1d5db transparent" }}
                >
                  {filterOptions.colors.map((color) => {
                    const getColorClass = (colorName: string): string => {
                      if (colorName === "All") return "bg-gray-200 border-2 border-gray-400";
                      
                      const colorClasses: { [key: string]: string } = {
                        Black: "bg-black",
                        White: "bg-white border-2 border-gray-300",
                        Red: "bg-red-500",
                        Blue: "bg-blue-500",
                        Navy: "bg-blue-900",
                        Green: "bg-green-500",
                        Yellow: "bg-yellow-400",
                        Orange: "bg-orange-500",
                        Purple: "bg-purple-500",
                        Pink: "bg-pink-500",
                        Gray: "bg-gray-500",
                        Grey: "bg-gray-500",
                        Brown: "bg-amber-800",
                        Beige: "bg-amber-100 border-2 border-amber-300",
                        Cream: "bg-amber-50 border-2 border-amber-200",
                        Khaki: "bg-yellow-600",
                        "Multi colored": "bg-gradient-to-r from-red-400 via-yellow-400 to-purple-400",
                        "African Prints": "bg-gradient-to-r from-orange-400 via-red-400 to-yellow-400",
                        Silver: "bg-gray-300",
                        Gold: "bg-yellow-400",
                        Transparent: "bg-gray-100 border-2 border-gray-300",
                        Tan: "bg-amber-600",
                        Nude: "bg-rose-200",
                        Burgundy: "bg-red-800",
                        Olive: "bg-green-700",
                      };
                      
                      return colorClasses[colorName] || "bg-gray-400";
                    };

                    return (
                      <button
                        key={color}
                        onClick={() => handleRadioChange("color", color)}
                        className={cn(
                          "w-6 h-6 rounded-full cursor-pointer transition-all duration-300 border-2 border-white shadow-sm hover:scale-110",
                          getColorClass(color),
                          filters.color === color
                            ? "ring-2 ring-black ring-offset-1"
                            : "shadow-md"
                        )}
                        disabled={isLoading}
                        title={color}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Brand Filter - Hidden for brand pages */}
          {filterOptions.brands.length > 0 && (
            <div className="mb-4">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleSection("brand")}
                  className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between border-b border-gray-200"
                  disabled={isLoading}
                >
                  <span className="text-sm font-semibold text-gray-900">
                    Brand
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 text-gray-600 transition-transform duration-300",
                      openSections.brand ? "rotate-180" : "-rotate-90"
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "transition-all duration-300 ease-in-out overflow-hidden bg-white",
                    openSections.brand
                      ? "max-h-96 py-4 px-5"
                      : "max-h-0 py-0 px-5"
                  )}
                >
                  <div className="space-y-4">
                    <div
                      className={cn(
                        "space-y-0",
                        filterOptions.brands.length > 6
                          ? "max-h-48 overflow-y-auto pr-2 scrollbar-light"
                          : ""
                      )}
                      style={filterOptions.brands.length > 6 ? {
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#d1d5db transparent'
                      } : undefined}
                    >
                      <h4 className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3 sticky top-0 bg-white py-1">
                        Top Brands this week
                      </h4>
                      {filterOptions.brands.slice(0, 5).map((brand) => (
                        <label
                          key={brand}
                          className="flex items-center cursor-pointer py-2 text-sm group"
                        >
                          <input
                            type="checkbox"
                            checked={filters.brand === brand}
                            onChange={() => handleCheckboxChange("brand", brand)}
                            className="mr-2.5 accent-black"
                            disabled={isLoading}
                          />
                          <span className="text-gray-700 group-hover:text-gray-900 transition-colors font-medium tracking-wide uppercase">
                            {brand}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Price Filter */}
          <div className="mb-4">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection("price")}
                className="w-full px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 flex items-center justify-between border-b border-gray-200"
                disabled={isLoading}
              >
                <span className="text-sm font-semibold text-gray-900">
                  Price
                </span>
                <ChevronDown
                  className={cn(
                    "h-3 w-3 text-gray-600 transition-transform duration-300",
                    openSections.price ? "rotate-180" : "-rotate-90"
                  )}
                />
              </button>
              <div
                className={cn(
                  "transition-all duration-300 ease-in-out overflow-hidden bg-white",
                  openSections.price
                    ? "max-h-96 py-4 px-5"
                    : "max-h-0 py-0 px-5"
                )}
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1.5">Min Price</label>
                      <input
                        type="number"
                        value={priceValues[0]}
                        onChange={(e) => {
                          const newMin = parseInt(e.target.value) || actualMinPrice;
                          handlePriceChange([newMin, priceValues[1]]);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        placeholder="Min"
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1.5">Max Price</label>
                      <input
                        type="number"
                        value={priceValues[1]}
                        onChange={(e) => {
                          const newMax = parseInt(e.target.value) || actualMaxPrice;
                          handlePriceChange([priceValues[0], newMax]);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                        placeholder="Max"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="px-1 pt-2">
                    <Slider
                      value={priceValues}
                      onValueChange={handlePriceChange}
                      min={priceRange[0]}
                      max={priceRange[1]}
                      step={10000}
                      className="w-full [&_[role=slider]]:bg-black [&_[role=slider]]:border-black [&_.bg-primary]:bg-black"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>UGX {priceRange[0].toLocaleString()}</span>
                    <span>UGX {priceRange[1].toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Style Filter - Only show if options exist */}
          {filterOptions.styles && filterOptions.styles.length > 0 && (
            <FilterRadioGroup
              title="Style"
              options={filterOptions.styles}
              filterKey="style"
              sectionKey="style"
            />
          )}

          {/* Occasion Filter - Only show if options exist */}
          {filterOptions.occasions && filterOptions.occasions.length > 0 && (
            <FilterRadioGroup
              title="Occasion"
              options={filterOptions.occasions}
              filterKey="occasion"
              sectionKey="occasion"
            />
          )}

          {/* Material Filter - Only show if options exist */}
          {filterOptions.materials && filterOptions.materials.length > 0 && (
            <FilterRadioGroup
              title="Material"
              options={filterOptions.materials}
              filterKey="material"
              sectionKey="material"
            />
          )}

          {/* Fitting Filter - Only show if options exist */}
          {filterOptions.fittings && filterOptions.fittings.length > 0 && (
            <FilterRadioGroup
              title="Fitting"
              options={filterOptions.fittings}
              filterKey="fitting"
              sectionKey="fitting"
            />
          )}
        </div>

        {/* Clear Filters */}
        {activeFiltersCount > 0 && (
          <div className="pt-4 border-t border-gray-200 flex-shrink-0 mt-4">
            <Button
              onClick={onClearFilters}
              variant="ghost"
              className="text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors font-medium min-h-[44px] justify-start px-0 w-full"
              disabled={isLoading}
            >
              <X className="h-4 w-4 mr-2" />
              Clear all filters ({activeFiltersCount})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
